import React from 'react';
import { cn } from '@/lib/cn';
import type { Building } from '@repo/api';
import { Image } from '@/components/ui/Image';
import { Rating } from '@/components/ui';
import BuildingCardTags from './BuildingCardTags';

export interface BuildingCardProps {
  /** Core building data from API */
  building: Building;
  /** Primary image to display for the building */
  imageUrl?: string;
  /** Alt text for the building image */
  imageAlt?: string;
  /** Optional className to customize container */
  className?: string;
  /** Optional number of tags to display */
  displayTagsCount?: number;
  /** Control Next.js image priority for the primary card image */
  imagePriority?: boolean;
  /** Control image fetch priority for LCP-sensitive contexts */
  imageFetchPriority?: 'high' | 'low' | 'auto';
  /** Control native loading behavior for the primary card image */
  imageLoading?: 'eager' | 'lazy';
  /** Control image quality for card images */
  imageQuality?: number;
}

export function BuildingCard({
  building,
  imageUrl,
  imageAlt,
  className,
  displayTagsCount = 1,
  imagePriority = false,
  imageFetchPriority = 'auto',
  imageLoading = 'lazy',
  imageQuality = 70,
}: BuildingCardProps) {
  const hasReviews =
    typeof building.averageRating === 'number' && typeof building.reviewCount === 'number';

  const title = building.name || building.primaryAddress?.streetAddress || 'Building';
  const neighborhood = building.neighborhoodName;

  return (
    <div className={cn('flex flex-col gap-2 w-full', className)}>
      {/* Image Container */}
      <div className={cn('relative w-full shadow-md', 'bg-primary overflow-hidden rounded-2xl')}>
        {imageUrl ? (
          <Image
            aspectRatio="4:3"
            quality={imageQuality}
            src={imageUrl}
            className="w-full"
            alt={imageAlt || title}
            // `200px` was too small for desktop cards (caused blurry upscaling).
            // Match the typical carousel card size more closely.
            sizes="(max-width: 768px) calc(100vw - 104px), (max-width: 1280px) 33vw, 280px"
            priority={imagePriority}
            fetchPriority={imageFetchPriority}
            loading={imageLoading}
          />
        ) : (
          <div className="w-full bg-secondary" style={{ aspectRatio: '4 / 3' }} />
        )}

        {/* Tags Overlay - Top Left */}
        <BuildingCardTags building={building} displayTagsCount={displayTagsCount} />

        {/* Save button - top right */}
        {/* BE doesn't have save building functionality yet */}
        {/* <div className="absolute top-3 right-3">
          <SaveButton
            isSaved={false}
            className={cn(
              'bg-[rgba(255,255,255,0.9)] rounded-full p-2',
              'shadow-[0px_4px_8px_-2px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.06)]',
            )}
            iconClassName="w-4 h-4"
            aria-label="Save building"
          />
        </div> */}
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-1 w-full">
        {/* Top Row: Name + Rating */}
        <div className="flex items-center gap-4 w-full">
          <h3 className="flex-1 min-w-0 text-body-2-high text-primary truncate">{title}</h3>
          {hasReviews && <Rating value={building.averageRating} count={building.reviewCount} />}
        </div>

        {/* Address */}
        {neighborhood ? (
          <p className="text-body-2 text-secondary truncate w-full">{neighborhood}</p>
        ) : null}
      </div>
    </div>
  );
}
